import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import chatService, { ChatMessage, ChatChannel } from '../services/chatService';
import langchainService, { AIConfig } from '../services/langchainService';
import memberService from '../services/memberService';
import storageService from '../services/storageService';
import { collection, query, where, getDocs, doc, getDoc, setDoc, serverTimestamp, updateDoc, deleteDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DirectMessage, WorkspaceUser, UserRole, ChannelType, AIProvider, FilePreview } from '../types/chat.types';
import { getDMChannelId } from '../utils/chatUtils';
import errorLogger from '../utils/errorLogger';
import { toastManager } from '../components/common/Toast';

export const useTeamChat = () => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  
  // State
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [directMessages, setDirectMessages] = useState<DirectMessage[]>([]);
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUser[]>([]);
  const [currentChannel, setCurrentChannel] = useState<ChatChannel | null>(null);
  const [currentDM, setCurrentDM] = useState<DirectMessage | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [userRole, setUserRole] = useState<UserRole>('member');
  const [channelMembers, setChannelMembers] = useState<WorkspaceUser[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [filePreview, setFilePreview] = useState<FilePreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Load user role
  useEffect(() => {
    const getUserRole = async () => {
      if (currentWorkspace && user) {
        try {
          errorLogger.debug('Loading user role', {
            component: 'useTeamChat',
            action: 'getUserRole',
            userId: user.firebase_uid,
            workspaceId: currentWorkspace.id
          });
          
          const member = await memberService.getMemberByUserAndWorkspace(
            user.firebase_uid,
            currentWorkspace.id
          );
          if (member) {
            setUserRole(member.workspace_role as UserRole);
            errorLogger.success('User role loaded successfully', {
              component: 'useTeamChat',
              metadata: { role: member.workspace_role }
            });
          }
        } catch (error) {
          errorLogger.error('Failed to load user role', error as Error, {
            component: 'useTeamChat',
            action: 'getUserRole'
          });
          toastManager.error('역할 로드 실패', '사용자 권한을 확인할 수 없습니다.');
        }
      }
    };
    getUserRole();
  }, [currentWorkspace, user]);

  // Load channels function
  const loadChannels = useCallback(async () => {
    if (!currentWorkspace || !user) return;
    
    try {
      errorLogger.debug('Loading channels', {
        component: 'useTeamChat',
        workspaceId: currentWorkspace.id,
        userId: user.firebase_uid
      });
      
      const channelList = await chatService.getWorkspaceChannels(currentWorkspace.id, user.firebase_uid);
      
      // If no channels exist, create the default 'general' channel
      if (channelList.length === 0) {
        console.log('No channels found, creating general channel');
        const generalChannelId = await chatService.createChannel({
          workspace_id: currentWorkspace.id,
          name: 'general',
          description: '모든 멤버를 위한 일반 채널',
          type: 'public',
          members: [user.firebase_uid],
          owner_id: user.firebase_uid
        });
        
        // Reload channels after creating general
        const updatedChannelList = await chatService.getWorkspaceChannels(currentWorkspace.id, user.firebase_uid);
        setChannels(updatedChannelList);
        
        // Select the general channel
        const generalChannel = updatedChannelList.find(ch => ch.id === generalChannelId);
        if (generalChannel) {
          setCurrentChannel(generalChannel);
        }
      } else {
        setChannels(channelList);
        
        // 기본 채널 선택
        if (!currentChannel && !currentDM) {
          const generalChannel = channelList.find(ch => ch.name === 'general') || channelList[0];
          setCurrentChannel(generalChannel);
        }
      }
      
      errorLogger.success('Channels loaded', {
        component: 'useTeamChat',
        metadata: { channelCount: channels.length }
      });
    } catch (error) {
      errorLogger.error('Failed to load channels', error as Error, {
        component: 'useTeamChat'
      });
      toastManager.error('채널 로드 실패', '채널 목록을 불러올 수 없습니다.');
    }
  }, [currentWorkspace, user, currentChannel, currentDM, channels.length]);

  // Load channels on workspace change
  useEffect(() => {
    if (currentWorkspace && user) {
      loadChannels();
    }
  }, [currentWorkspace, user, loadChannels]);

  // Load workspace users
  useEffect(() => {
    const loadWorkspaceUsers = async () => {
      if (!currentWorkspace || !user) return;
      
      try {
        const members = await memberService.getWorkspaceMembers(currentWorkspace.id);

        // Fetch user details for each member (including current user)
        const usersPromises = members
          .map(async (member) => {
            try {
              // Firestore에서 실제 사용자 정보 가져오기
              const userDoc = await getDoc(doc(db, 'users', member.user_id));
              const userData = userDoc.data();

              return {
                id: member.user_id,
                name: member.workspace_profile?.display_name || userData?.name || userData?.display_name || 'Unknown User',
                email: userData?.email || 'No email',
                avatar_url: userData?.profile_image_url || userData?.avatar_url,
                is_online: false,
                position: member.workspace_profile?.position,
                department: member.workspace_profile?.department,
                joined_at: member.joined_at?.toDate?.() || new Date()
              };
            } catch (error) {
              console.error(`Error loading user ${member.user_id}:`, error);
              return {
                id: member.user_id,
                name: member.workspace_profile?.display_name || 'Unknown User',
                email: 'No email',
                avatar_url: undefined,
                is_online: false,
                position: member.workspace_profile?.position,
                department: member.workspace_profile?.department,
                joined_at: member.joined_at?.toDate?.() || new Date()
              };
            }
          });

        const users = await Promise.all(usersPromises);
        setWorkspaceUsers(users);
      } catch (error) {
        console.error('Error loading workspace users:', error);
      }
    };
    
    loadWorkspaceUsers();
  }, [currentWorkspace, user]);

  // Load direct messages
  useEffect(() => {
    const loadDirectMessages = async () => {
      if (!currentWorkspace || !user) return;
      
      try {
        const dms: DirectMessage[] = [];

        // Filter out current user for DM list
        for (const workspaceUser of workspaceUsers.filter(u => u.id !== user.firebase_uid)) {
          const dmChannelId = getDMChannelId(user.firebase_uid, workspaceUser.id);
          const dmChannelRef = doc(db, 'direct_messages', dmChannelId);
          const dmChannelDoc = await getDoc(dmChannelRef);
          
          if (dmChannelDoc.exists()) {
            const data = dmChannelDoc.data();
            dms.push({
              id: dmChannelId,
              user_id: workspaceUser.id,
              user_name: workspaceUser.name,
              user_email: workspaceUser.email,
              avatar_url: workspaceUser.avatar_url,
              last_message: data.last_message,
              last_message_time: data.last_message_time?.toDate(),
              unread_count: data[`unread_${user.firebase_uid}`] || 0,
              is_online: workspaceUser.is_online
            });
          }
        }
        
        setDirectMessages(dms.sort((a, b) => {
          if (!a.last_message_time) return 1;
          if (!b.last_message_time) return -1;
          return b.last_message_time.getTime() - a.last_message_time.getTime();
        }));
      } catch (error) {
        console.error('Error loading direct messages:', error);
      }
    };
    
    loadDirectMessages();
  }, [workspaceUsers, currentWorkspace, user]);

  // Load messages with real-time updates
  useEffect(() => {
    if (currentChannel && currentWorkspace) {
      // Set up real-time listener for channel messages
      const messagesRef = collection(db, 'workspaces', currentWorkspace.id, 'channels', currentChannel.id, 'messages');
      const q = query(messagesRef);
      
      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const messageList: ChatMessage[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            messageList.push({
              id: doc.id,
              channel_id: currentChannel.id,
              workspace_id: currentWorkspace.id,
              user_id: data.user_id,
              user_name: data.user_name,
              user_avatar: data.user_avatar,
              content: data.content,
              type: data.type || 'text',
              attachments: data.attachments,
              created_at: data.created_at,
              edited_at: data.edited_at,
              is_ai: data.is_ai || false,
              ai_model: data.ai_model
            } as ChatMessage);
          });
          
          setMessages(messageList.sort((a, b) => {
            const aTime = a.created_at?.toDate?.() || new Date(0);
            const bTime = b.created_at?.toDate?.() || new Date(0);
            return aTime.getTime() - bTime.getTime();
          }));
        },
        (error) => {
          errorLogger.error('Failed to load messages', error as Error, {
            component: 'useTeamChat',
            action: 'loadMessages'
          });
          toastManager.error('메시지 로드 실패', '실시간 메시지 업데이트에 문제가 발생했습니다.');
        }
      );
      
      return () => unsubscribe();
    } else if (currentDM && user) {
      // Set up real-time listener for DM messages
      const messagesRef = collection(db, 'direct_messages', currentDM.id, 'messages');
      const q = query(messagesRef);
      
      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const messageList: ChatMessage[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            messageList.push({
              id: doc.id,
              channel_id: currentDM.id,
              workspace_id: '',
              user_id: data.user_id,
              user_name: data.user_name,
              user_avatar: data.user_avatar,
              content: data.content,
              type: data.type || 'text',
              attachments: data.attachments,
              created_at: data.created_at,
              edited_at: data.edited_at,
              is_ai: data.is_ai || false
            } as ChatMessage);
          });
          
          setMessages(messageList.sort((a, b) => {
            const aTime = a.created_at?.toDate?.() || new Date(0);
            const bTime = b.created_at?.toDate?.() || new Date(0);
            return aTime.getTime() - bTime.getTime();
          }));
        },
        (error) => {
          console.error('Error loading DM messages:', error);
        }
      );
      
      return () => unsubscribe();
    } else {
      // Clear messages when no channel or DM is selected
      setMessages([]);
    }
  }, [currentChannel, currentDM, currentWorkspace, user]);

  // Channel operations
  const createChannel = useCallback(async (
    name: string,
    description: string,
    type: ChannelType,
    memberIds: string[]
  ) => {
    if (!currentWorkspace || !user) return;
    
    await chatService.createChannel({
      workspace_id: currentWorkspace.id,
      name,
      description,
      type,
      members: [user.firebase_uid, ...memberIds],
      owner_id: user.firebase_uid
    });
    
    const updatedChannels = await chatService.getWorkspaceChannels(currentWorkspace.id, user.firebase_uid);
    setChannels(updatedChannels);
  }, [currentWorkspace, user]);

  const deleteChannel = useCallback(async (channelId: string) => {
    if (!currentWorkspace) return;
    
    await deleteDoc(doc(db, 'workspaces', currentWorkspace.id, 'channels', channelId));
    
    const updatedChannels = await chatService.getWorkspaceChannels(currentWorkspace.id, user?.firebase_uid || '');
    setChannels(updatedChannels);
    
    if (currentChannel?.id === channelId) {
      setCurrentChannel(updatedChannels[0] || null);
    }
  }, [currentWorkspace, currentChannel, user]);

  // Message operations
  const sendMessage = useCallback(async (content: string, attachments: Array<{url: string; name: string}> = []) => {
    if (!user || !currentWorkspace) {
      console.log('❌ Cannot send message: missing user or workspace');
      return;
    }

    if (currentChannel) {
      console.log(`💬 Sending message to channel: ${currentChannel.name}`);
      console.log(`📎 Attachments: ${attachments.length}`);

      await chatService.sendMessage({
        channel_id: currentChannel.id,
        workspace_id: currentWorkspace.id,
        user_id: user.firebase_uid,
        user_name: user.displayName || user.name || 'Unknown User',
        user_avatar: user.avatar_url,
        content,
        type: 'text',
        attachments: attachments.length > 0 ? attachments.map(att => ({
          url: att.url,
          type: 'file',
          name: att.name,
          preview: att.url
        })) : undefined
      });
      console.log('✅ Channel message sent successfully');
    } else if (currentDM) {
      console.log(`💬 [DM Send] Sending DM to: ${currentDM.user_name}`);
      console.log(`📎 [DM Send] Attachments count: ${attachments.length}`);
      console.log(`📝 [DM Send] Content: "${content}"`);

      const dmRef = doc(db, 'direct_messages', currentDM.id);
      const messagesRef = collection(dmRef, 'messages');

      // Prepare attachments with detailed logging
      const processedAttachments = attachments.length > 0 ? attachments.map((att, index) => {
        console.log(`📎 [DM Send] Attachment ${index + 1}:`, {
          name: att.name,
          url: att.url,
          urlLength: att.url?.length || 0
        });
        return {
          url: att.url,
          type: 'file',
          name: att.name
        };
      }) : [];

      const messageData = {
        user_id: user.firebase_uid,
        user_name: user.displayName || user.name || 'Unknown User',
        user_avatar: user.avatar_url,
        content,
        attachments: processedAttachments,
        created_at: serverTimestamp(),
        is_ai: false
      };

      console.log(`📤 [DM Send] Message data prepared:`, {
        user_id: messageData.user_id,
        content_length: content.length,
        attachments_count: processedAttachments.length,
        has_attachments: processedAttachments.length > 0
      });

      await setDoc(doc(messagesRef), messageData);
      console.log('✅ [DM Send] DM message saved to Firestore');

      // Update last message info
      const lastMessage = attachments.length > 0
        ? `${content || ''}${content && attachments.length > 0 ? ' ' : ''}📎 ${attachments.length}개의 파일`
        : content;

      console.log(`📝 [DM Send] Updating DM metadata with last_message: "${lastMessage}"`);
      await updateDoc(dmRef, {
        last_message: lastMessage,
        last_message_time: serverTimestamp(),
        [`unread_${currentDM.user_id}`]: (currentDM.unread_count || 0) + 1
      });
      console.log('✅ [DM Send] DM metadata updated successfully');
    } else {
      console.log('❌ Cannot send message: no channel or DM selected');
    }
  }, [user, currentWorkspace, currentChannel, currentDM]);

  const deleteMessage = useCallback(async (messageId: string) => {
    if (!currentWorkspace || !currentChannel) return;
    
    await deleteDoc(
      doc(db, 'workspaces', currentWorkspace.id, 'channels', currentChannel.id, 'messages', messageId)
    );
  }, [currentWorkspace, currentChannel]);

  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    if (!currentWorkspace || !currentChannel) return;
    
    await updateDoc(
      doc(db, 'workspaces', currentWorkspace.id, 'channels', currentChannel.id, 'messages', messageId),
      {
        content: newContent,
        edited_at: serverTimestamp()
      }
    );
  }, [currentWorkspace, currentChannel]);

  // File operations
  const handleFileUpload = useCallback(async (files: FileList) => {
    const newFiles = Array.from(files);
    console.log(`📎 [DM File Upload] Handler called with ${newFiles.length} files`);
    console.log(`📎 [Context] Channel: ${currentChannel?.name || 'none'}, DM: ${currentDM?.user_name || 'none'}`);

    newFiles.forEach((file, index) => {
      console.log(`  ${index + 1}. ${file.name} (${(file.size / 1024).toFixed(1)} KB, ${file.type})`);
    });

    setUploadedFiles(prev => {
      const updated = [...prev, ...newFiles];
      console.log(`📎 [State Update] Total files in queue: ${updated.length}`);
      return updated;
    });

    const previews: FilePreview[] = [];
    for (const file of newFiles) {
      if (file.type.startsWith('image/')) {
        console.log(`🖼️ [Preview] Generating preview for image: ${file.name}`);
        const reader = new FileReader();
        reader.onload = (e) => {
          console.log(`✅ [Preview] Preview generated for: ${file.name}`);
          previews.push({ url: e.target?.result as string, type: 'image' });
          setFilePreview(prev => {
            const updated = [...prev, { url: e.target?.result as string, type: 'image' }];
            console.log(`📎 [Preview State] Total previews: ${updated.length}`);
            return updated;
          });
        };
        reader.onerror = (error) => {
          console.error(`❌ [Preview] Failed to generate preview for ${file.name}:`, error);
        };
        reader.readAsDataURL(file);
      }
    }
  }, [currentChannel, currentDM]);

  const uploadFiles = useCallback(async (): Promise<Array<{url: string; name: string; file: File; type: string}>> => {
    if (uploadedFiles.length === 0) {
      console.log('📎 No files to upload');
      return [];
    }

    console.log(`📎 Starting upload of ${uploadedFiles.length} files...`);
    setIsUploading(true);
    const uploadResults: Array<{url: string; name: string; file: File; type: string}> = [];

    try {
      // Determine the upload path based on current context
      if (currentChannel && currentWorkspace) {
        // For channel messages
        console.log(`📎 Uploading to channel: ${currentChannel.name} (${currentChannel.id})`);
        const results = await storageService.uploadMultipleFiles(
          uploadedFiles,
          currentWorkspace.id,
          currentChannel.id
        );

        for (const result of results) {
          uploadResults.push({
            url: result.url,
            name: result.originalName,
            file: result.file,
            type: result.file.type
          });
        }
      } else if (currentDM) {
        // For direct messages - use a simpler path structure
        console.log(`📎 [DM Upload] Starting DM file upload`);
        console.log(`📎 [DM Upload] Target DM: ${currentDM.user_name} (${currentDM.id})`);
        console.log(`📎 [DM Upload] Files to upload: ${uploadedFiles.length}`);

        for (const file of uploadedFiles) {
          console.log(`📎 [DM Upload] Processing file: ${file.name} (${(file.size / 1024).toFixed(1)} KB, ${file.type})`);
          try {
            const uploadPath = `dm-attachments/${currentDM.id}`;
            console.log(`📎 [DM Upload] Upload path: ${uploadPath}`);

            const result = await storageService.uploadFile(file, uploadPath);
            console.log(`✅ [DM Upload] File uploaded successfully!`);
            console.log(`   - URL: ${result.url}`);
            console.log(`   - Name: ${result.originalName}`);

            uploadResults.push({
              url: result.url,
              name: result.originalName,
              file: file,
              type: file.type
            });
            console.log(`📎 [DM Upload] Added to results. Total: ${uploadResults.length}`);
          } catch (uploadError) {
            console.error(`❌ [DM Upload] Failed to upload ${file.name}:`, uploadError);
            console.error(`   - Error details:`, uploadError);
            toastManager.error('파일 업로드 실패', `${file.name} 업로드 중 오류 발생`);
          }
        }
        console.log(`📎 [DM Upload] Completed. Successfully uploaded: ${uploadResults.length}/${uploadedFiles.length}`);
      } else {
        // Fallback to general chat attachments
        console.log('📎 Uploading to general chat attachments');
        for (const file of uploadedFiles) {
          const result = await storageService.uploadFile(file, 'chat-attachments');
          uploadResults.push({
            url: result.url,
            name: result.originalName,
            file: file,
            type: file.type
          });
        }
      }

      console.log(`✅ Successfully uploaded ${uploadResults.length}/${uploadedFiles.length} files`);
      setUploadedFiles([]);
      setFilePreview([]);
    } catch (error) {
      console.error('❌ Error uploading files:', error);
      toastManager.error('파일 업로드 실패', '파일을 업로드하는 중 오류가 발생했습니다.');
      throw error;
    } finally {
      setIsUploading(false);
    }

    return uploadResults;
  }, [uploadedFiles, currentChannel, currentWorkspace, currentDM]);

  const removeFile = useCallback((index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreview(prev => prev.filter((_, i) => i !== index));
  }, []);

  // AI operations
  const sendAIMessage = useCallback(async (
    message: string,
    provider: AIProvider,
    model: string,
    attachments?: Array<{ url: string; type: string; name?: string; file?: File }>
  ) => {
    if (!user || !currentWorkspace || !currentChannel) return;

    // 임시 "생성 중" 메시지 생성 - type을 'thinking'으로 설정
    let thinkingMessageRef: string | null = null;
    try {
      thinkingMessageRef = await chatService.sendMessage({
        channel_id: currentChannel.id,
        workspace_id: currentWorkspace.id,
        user_id: 'ai-assistant',
        user_name: 'AXTI',
        content: '답변을 생성 중입니다...',
        type: 'thinking' as any, // Special type for thinking messages
        ai_model: model
      });
      console.log('Created thinking message with ID:', thinkingMessageRef);
    } catch (error) {
      console.error('Failed to create thinking message:', error);
    }

    try {
      // Check if user is asking about meetings
      const messageLower = message.toLowerCase();
      const isMeetingQuery = messageLower.includes('회의') ||
                            messageLower.includes('미팅') ||
                            messageLower.includes('meeting') ||
                            messageLower.includes('오늘') ||
                            messageLower.includes('예정') ||
                            messageLower.includes('일정') ||
                            messageLower.includes('액션') ||
                            messageLower.includes('action');

      let aiResponse = '';

      // If it's a meeting query, get meeting data first
      if (isMeetingQuery) {
        const meetingInfo = await langchainService.getMeetingData(currentWorkspace.id, message);

        // If we have meeting data, use it as the response
        if (meetingInfo && !meetingInfo.includes('오류')) {
          aiResponse = meetingInfo;
        }
      }

      // If we don't have a meeting response, use backend API for AI processing
      if (!aiResponse) {
      // Get recent messages for context (conversation history)
      const recentMessages = messages.slice(-30); // Last 15 exchanges
      const conversationHistory = recentMessages.map(msg => ({
        role: msg.type === 'ai' ? 'assistant' : 'user',
        content: msg.content
      }));

      // Use backend API (same as personal chat for security and CORS)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://us-central1-teampulse-61474.cloudfunctions.net/api';

      // Prepare request body
      const requestBody: any = {
        message,
        agentId: 'team-assistant',
        model: model,
        systemPrompt: `You are AXTI, a helpful AI assistant in AXTIV team chat.

Current time (Korea): ${new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}

Guidelines for responses:
- Provide detailed, comprehensive answers with depth and context
- Include relevant examples, explanations, and background information when appropriate
- Break down complex topics into understandable sections using structured formatting (lists, headings)
- When asked about recent events, news, or real-time information, use web search to provide up-to-date answers
- Always cite sources when using web search results
- Be thorough while maintaining clarity and professional tone
- You can help with various tasks including scheduling, code review, creating polls, and answering questions`,
        workspaceId: currentWorkspace.id,
        channelId: currentChannel.id,
        conversationHistory: conversationHistory
      };

      // Add attachments if available (for multimodal support)
      if (attachments && attachments.length > 0) {
        // Process attachments with content extraction for non-image files
        const processedAttachments = [];

        for (const att of attachments) {
          const processedAtt: any = {
            url: att.url,
            type: att.type,
            name: att.name || 'attachment'
          };

          // Extract text content for non-image files if we have the file object
          if (att.file && !att.type?.startsWith('image/')) {
            try {
              console.log(`📄 Attempting to extract content from ${att.name} (type: ${att.type})`);

              // Use the new document processor service with LangChain
              const { documentProcessor } = await import('../services/documentProcessorService');
              const result = await documentProcessor.processFile(att.file);

              if (result && result.text) {
                processedAtt.content = result.text;
                console.log(`✅ Extracted content from ${att.name}: ${result.text.length} characters`);

                if (result.pageCount) {
                  console.log(`📑 Document has ${result.pageCount} pages`);
                }

                // For debugging: log first 500 characters
                const preview = documentProcessor.getSummary(result, 500);
                console.log(`Preview: ${preview}`);
              } else if (result.error) {
                console.warn(`⚠️ Error extracting from ${att.name}: ${result.error}`);
                // Try fallback to old method
                try {
                  const { parseDocumentLocally } = await import('../utils/pdfParser');
                  const fallbackResult = await parseDocumentLocally(att.file);
                  if (fallbackResult && fallbackResult.text) {
                    processedAtt.content = fallbackResult.text;
                    console.log(`✅ Fallback extraction successful: ${fallbackResult.text.length} characters`);
                  }
                } catch (fallbackError) {
                  console.error(`Fallback also failed:`, fallbackError);
                }
              } else {
                console.warn(`⚠️ No text extracted from ${att.name}`);
              }
            } catch (error) {
              console.error(`❌ Failed to extract content from ${att.name}:`, error);
              // Add URL-based content note for AI
              if (att.url) {
                processedAtt.content = `[파일 내용을 직접 추출할 수 없습니다. 파일 URL: ${att.url}]`;
              }
            }
          } else if (att.type?.startsWith('image/')) {
            console.log(`🖼️ Image file detected: ${att.name}, will be processed by vision model`);
          }

          processedAttachments.push(processedAtt);
        }

        requestBody.attachments = processedAttachments;
      }

      try {
        const response = await fetch(`${apiUrl}/agents/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        aiResponse = data.response || data.content || 'Sorry, I could not generate a response.';
      } catch (error) {
        console.error('Error calling backend API:', error);
        aiResponse = 'Sorry, I encountered an error while processing your request. Please try again.';
      }
      }

      // 임시 메시지 삭제
      if (thinkingMessageRef) {
        console.log('Deleting thinking message with ID:', thinkingMessageRef);
        try {
          await chatService.deleteMessage(currentWorkspace.id, currentChannel.id, thinkingMessageRef);
          console.log('Successfully deleted thinking message');
        } catch (deleteError) {
          console.error('Failed to delete thinking message:', deleteError);
          // Try alternative: directly update the message to mark as deleted
          try {
            const messageRef = doc(db, 'workspaces', currentWorkspace.id, 'channels', currentChannel.id, 'messages', thinkingMessageRef);
            await updateDoc(messageRef, {
              deleted: true,
              deleted_at: serverTimestamp()
            });
            console.log('Deleted thinking message via direct update');
          } catch (directError) {
            console.error('Failed to delete via direct update:', directError);
          }
        }
      }

      // 실제 AI 응답 전송
      await chatService.sendMessage({
        channel_id: currentChannel.id,
        workspace_id: currentWorkspace.id,
        user_id: 'ai-assistant',
        user_name: `AXTI (${model})`,
        content: aiResponse,
        type: 'ai',
        ai_model: model
      });
    } catch (error) {
      console.error('Error in sendAIMessage:', error);

      // 에러 발생 시에도 임시 메시지 삭제
      if (thinkingMessageRef) {
        console.log('Deleting thinking message (error case) with ID:', thinkingMessageRef);
        try {
          await chatService.deleteMessage(currentWorkspace.id, currentChannel.id, thinkingMessageRef);
          console.log('Successfully deleted thinking message (error case)');
        } catch (deleteError) {
          console.error('Failed to delete thinking message (error case):', deleteError);
          // Try alternative: directly update the message to mark as deleted
          try {
            const messageRef = doc(db, 'workspaces', currentWorkspace.id, 'channels', currentChannel.id, 'messages', thinkingMessageRef);
            await updateDoc(messageRef, {
              deleted: true,
              deleted_at: serverTimestamp()
            });
            console.log('Deleted thinking message via direct update (error case)');
          } catch (directError) {
            console.error('Failed to delete via direct update (error case):', directError);
          }
        }
      }

      // 에러 메시지 전송
      await chatService.sendMessage({
        channel_id: currentChannel.id,
        workspace_id: currentWorkspace.id,
        user_id: 'ai-assistant',
        user_name: `AXTI (${model})`,
        content: 'Sorry, I encountered an error while processing your request. Please try again.',
        type: 'ai',
        ai_model: model
      });
    }
  }, [user, currentWorkspace, currentChannel, messages]);

  return {
    // State
    channels,
    directMessages,
    workspaceUsers,
    currentChannel,
    currentDM,
    messages,
    userRole,
    channelMembers,
    uploadedFiles,
    filePreview,
    isUploading,
    
    // Setters
    setCurrentChannel,
    setCurrentDM,
    setChannelMembers,
    setUploadedFiles,
    setFilePreview,

    // Operations
    createChannel,
    deleteChannel,
    sendMessage,
    deleteMessage,
    editMessage,
    handleFileUpload,
    uploadFiles,
    removeFile,
    sendAIMessage,
    loadChannels
  };
};